/**
 * Evaluates a single tier-reset condition against a row/document state.
 */
const evaluateTierResetCondition = (rowState, condition) => {
    if (!condition) {
        return false;
    }

    const elementKey = condition.selectedConditionDataElement;
    const operator = condition.selectedConditionOperator;
    const rawValue = rowState?.[elementKey];

    if (operator === 'is_equals') {
        return rawValue === condition.value;
    }

    if (operator === 'is_there') {
        if (rawValue === undefined || rawValue === null) {
            return false;
        }
        if (typeof rawValue === 'string') {
            return rawValue.trim() !== '';
        }
        if (Array.isArray(rawValue)) {
            return rawValue.length > 0;
        }
        return true;
    }

    if (operator === 'any_of') {
        const allowedValues = condition.value;
        if (!Array.isArray(allowedValues)) {
            return false;
        }
        return allowedValues.includes(rawValue);
    }

    return false;
};

/**
 * Returns true when a tier-reset config matches the given row state.
 */
export const tierResetConfigMatches = (rowState, config) => {
    const rootMatches = evaluateTierResetCondition(rowState, {
        selectedConditionDataElement: config.selectedConditionDataElement,
        selectedConditionOperator: config.selectedConditionOperator,
        value: config.value,
    });

    if (!rootMatches) {
        return false;
    }

    return (config.actionButtonShowFields || []).every(
        (condition) => evaluateTierResetCondition(rowState, condition),
    );
};

/**
 * Resolves tier-reset config from chartData (supports platform key variants).
 */
export const resolveTierResetConfig = (chartData = {}) => {
    const candidates = [
        chartData.tierResetConfig,
        chartData.TIER_RESET_CONFIG,
        chartData.tabs?.dealReviewDetails?.TIER_RESET_CONFIG,
        chartData.tierResetConfig?.TIER_RESET_CONFIG,
    ];

    const matchedConfig = candidates.find((candidate) => Array.isArray(candidate) && candidate.length);
    return matchedConfig || [];
};

/**
 * Collects all field keys referenced by tier-reset rules for table API `fields`.
 */
export const getTierResetConfigFieldKeys = (tierResetConfig = []) => {
    const fieldKeys = new Set();

    if (!Array.isArray(tierResetConfig)) {
        return [];
    }

    tierResetConfig.forEach((config) => {
        if (config?.selectedConditionDataElement) {
            fieldKeys.add(config.selectedConditionDataElement);
        }

        if (config?.resetButtonField) {
            fieldKeys.add(config.resetButtonField);
        }

        if (config?.decisionField) {
            fieldKeys.add(config.decisionField);
        }

        (config.actionButtonShowFields || []).forEach((condition) => {
            if (condition?.selectedConditionDataElement) {
                fieldKeys.add(condition.selectedConditionDataElement);
            }
        });

        (config.onClickResetButtonResetFields || []).forEach((fieldKey) => {
            if (fieldKey) {
                fieldKeys.add(fieldKey);
            }
        });
    });

    return [...fieldKeys];
};

/**
 * Resolves the decision field key that owns the reset action for a config entry.
 */
export const getTierResetDecisionFieldForButton = (config) => {
    if (config?.resetButtonField) {
        return config.resetButtonField;
    }

    if (config?.decisionField) {
        return config.decisionField;
    }

    const fields = config.actionButtonShowFields || [];
    const decisionField = fields.find(
        (field) => /decision/i.test(field.selectedConditionDataElement || ''),
    );

    if (decisionField?.selectedConditionDataElement) {
        return decisionField.selectedConditionDataElement;
    }

    const firstConditionField = fields.find((field) => field?.selectedConditionDataElement);
    return firstConditionField?.selectedConditionDataElement || null;
};

/**
 * Returns field keys that should show the tier-reset action for a row.
 */
export const getTierResetFieldsForRow = (rowState = {}, tierResetConfig = []) => {
    const resetFields = new Set();

    if (!Array.isArray(tierResetConfig) || !tierResetConfig.length) {
        return resetFields;
    }

    tierResetConfig.forEach((config) => {
        if (!tierResetConfigMatches(rowState, config)) {
            return;
        }

        const decisionField = getTierResetDecisionFieldForButton(config);
        if (decisionField) {
            resetFields.add(decisionField);
        }
    });

    return resetFields;
};

/**
 * Returns true when a column should show the tier-reset icon for a row.
 */
export const isTierResetEnabledForField = (
    fieldKey,
    rowState = {},
    tierResetConfig = [],
    column = null,
) => {
    if (!fieldKey || !rowState?._id || !Array.isArray(tierResetConfig) || !tierResetConfig.length) {
        return false;
    }

    const resetFields = getTierResetFieldsForRow(rowState, tierResetConfig);
    if (resetFields.has(fieldKey)) {
        return true;
    }

    const showTierResetFlag = column?.showTierReset === true
        || column?.showTierReset === 'true'
        || column?.showTierReset === 'True';

    if (!showTierResetFlag) {
        return false;
    }

    return tierResetConfig.some((config) => {
        if (!tierResetConfigMatches(rowState, config)) {
            return false;
        }

        return getTierResetDecisionFieldForButton(config) === fieldKey;
    });
};
